import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AssetDto, FolderDto, GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { distinctUntilChanged, switchMap } from 'rxjs';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { GROWTHBOOK } from '../../../growthbook';
import { IsGallery } from '../../../lib/is-gallery';
import { ShareService } from '../../../services/share.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { ShareDirective } from '../../../shared-extra/directives/share.directive';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';

@Component({
  selector: 'app-share-dialog-social-footer',
  imports: [ButtonDirective, SvgComponent, ShareDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './share-dialog-social-footer.component.html',
  styleUrl: './share-dialog-social-footer.component.scss',
})
export class ShareDialogSocialFooterComponent {
  public growthBook = inject(GROWTHBOOK);
  private toasts = inject(ToastService);
  private state = inject(StateService);
  private shareService = inject(ShareService);
  gallery = input<GalleryDto>();
  folder = input<FolderDto>();
  asset = input<AssetDto>();
  item = computed(() => this.gallery() || this.folder());

  isTeamMember = computed(() => {
    const item = this.item();
    if (!item) return;
    const tenantId = (item.tenant as TenantDto)?.id || (item.tenant as string);
    return this.state.tenants?.some(t => t.id === tenantId);
  });

  canShare = computed(() => navigator.share && ScreenSizes.screenSize() !== 'desktop');

  shareLink = toSignal(
    toObservable(this.item).pipe(
      distinctUntilChanged((a, b) => (a?.path || a?.id) === (b?.path || b?.id)),
      switchMap(item => {
        if (IsGallery(item)) {
          return this.shareService.getShareLink(item as GalleryDto, { assetId: this.asset()?.id });
        }
        return this.shareService.getFolderShareLink(item as FolderDto);
      })
    )
  );

  emailHref = computed(() => {
    const item = this.item();
    const shareLink = this.shareLink()?.url;
    if (!item || !shareLink) return;
    return `mailto:?subject=${encodeURIComponent(item.title)}&body=${encodeURIComponent(this.getEmailText(item, shareLink))}`;
  });

  private socialText(item: GalleryDto | FolderDto, shareLink: string) {
    const activeWord = this.asset() ? 'asset' : IsGallery(item) ? 'gallery' : 'folder';
    if (item.password_enabled) {
      return `Hello, Here's the link to the ${activeWord}: ${shareLink}. The password is: ${item.password}`;
    } else {
      return `Hello, Here's the link to the ${activeWord}: ${shareLink}`;
    }
  }

  private getEmailText(item: GalleryDto | FolderDto, shareLink: string) {
    const activeWord = this.asset() ? 'asset' : IsGallery(item) ? 'gallery' : 'folder';
    if (item?.password_enabled) {
      return `Hello,\n\nHere's the link to the ${activeWord}: ${shareLink}\n\nThe password is: ${
        item.password
      }\n\nBest regards,\n${this.state.user?.first_name || ''}\n`;
    } else {
      return `Hello,\n\nHere's the link to the ${activeWord}: ${shareLink}\n\nBest regards,\n${
        this.state.user?.first_name || ''
      }\n`;
    }
  }

  whatsappHref = computed(() => {
    const shareLink = this.shareLink();
    const gallery = this.gallery();
    if (!shareLink || !gallery) return;
    return `https://api.whatsapp.com/send?text=${encodeURIComponent(this.socialText(gallery, shareLink.url))}`;
  });

  facebookHref = computed(() => {
    const shareLink = this.shareLink();
    if (!shareLink) return;
    return `https://www.facebook.com/dialog/feed?app_id=861804031074445&display=popup&link=${encodeURIComponent(shareLink.url)}`;
  });

  telegramHref = computed(() => {
    const shareLink = this.shareLink();
    const gallery = this.gallery();
    if (!shareLink || !gallery) return;
    return `https://telegram.me/share/url?url=${shareLink.url}&text=${this.socialText(gallery, shareLink.url)}`;
  });

  linkedinHref = computed(() => {
    const shareLink = this.shareLink();
    if (!shareLink) return;
    return `https://www.linkedin.com/shareArticle?mini=true&url=${shareLink.url}`;
  });

  onLinkCopied() {
    this.toasts.add('Link copied', 'success');
  }
}
